import { toTypedSchema } from '@vee-validate/zod'
import { useForm, useField } from 'vee-validate'
import type { NewsletterSubscriptionOptions, NewsletterSubscriptionSubmitPayload } from '@/types/NewsletterSubscription'
import { SEmailAddress } from '@/schemas/forms/Fields.schema'
import { ALREADY_SUBSCRIBED } from '@/utils/errorCodes'
import {
  DEV_EMARSYS_PARAMS,
  EMARSYS_COUNTRIES,
  EMARSYS_LANGUAGES,
  PROD_EMARSYS_PARAMS
} from '@/utils/constants/emarsys'

export const useNewsletterSubscription = (options: NewsletterSubscriptionOptions) => {
  const runtimeConfig = useRuntimeConfig()
  const EMARSYS_PARAMS = runtimeConfig.public.env === 'production' ? PROD_EMARSYS_PARAMS : DEV_EMARSYS_PARAMS
  const localeStore = useLocaleStore()
  const countriesStore = useCountriesStore()
  const languagesStore = useLanguagesStore()

  const { t } = useI18n()

  const isConfirmationModalOpen = ref(false)
  const isValid = ref(false)
  const responseError = ref<string>()
  const countryError = ref<string>()
  const languageError = ref<string>()

  const { errors, values, validate, resetForm } = useForm({
    validationSchema: toTypedSchema(SEmailAddress)
  })

  const { value: emailAddress } = useField<string>('emailAddress')

  const {
    value: privacyPolicyAccepted,
    errorMessage: privacyPolicyErrorMessage,
    setErrors: setPrivacyPolicyErrors
  } = useField<boolean>('privacyPolicyAccepted', {
    initialValue: false
  })

  watch(emailAddress, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      responseError.value = undefined
    }
  })

  const displayErrors = computed(() => ({
    emailAddress: errors.value.emailAddress ?? undefined,
    country: countryError.value,
    language: languageError.value,
    privacyPolicyAccepted:
      options.requirePrivacyPolicy && privacyPolicyErrorMessage.value ? privacyPolicyErrorMessage.value : undefined
  }))

  const closeConfirmationModal = () => {
    isConfirmationModalOpen.value = false
  }

  const clearCountryError = () => {
    countryError.value = undefined
  }

  const clearLanguageError = () => {
    languageError.value = undefined
  }

  const validatePrivacyPolicy = () => {
    if (!options.requirePrivacyPolicy) return true
    if (privacyPolicyAccepted.value) return true

    setPrivacyPolicyErrors([t('forms.global.error.required')])

    return false
  }

  const validateCountry = (selectedCountry?: string) => {
    if (!options.requireCountryValidation) return true

    const normalizedCountry = selectedCountry?.trim().toUpperCase()
    const countryCodes = countriesStore.sortedCountriesWithPopularFirst.map((country) => country.id.toUpperCase())
    const isCountryValid = normalizedCountry ? countryCodes.includes(normalizedCountry) : false

    if (isCountryValid) {
      clearCountryError()
      return true
    }

    countryError.value = 'forms.global.error.required'

    return false
  }

  const validateLanguage = (selectedLanguage?: string) => {
    if (!options.requireLanguageValidation) return true

    const normalizedLanguage = selectedLanguage?.trim().toLowerCase()
    const languageCodes = languagesStore.newsletterLanguages.map((language) => language.id.toLowerCase())
    const isLanguageValid = normalizedLanguage ? languageCodes.includes(normalizedLanguage) : false

    if (isLanguageValid) {
      clearLanguageError()
      return true
    }

    languageError.value = 'forms.global.error.required'

    return false
  }

  const onSubmit = async (payload: Partial<NewsletterSubscriptionSubmitPayload> = {}) => {
    responseError.value = undefined
    clearCountryError()
    clearLanguageError()

    const selectedCountry = payload.selectedCountry ?? localeStore.selectedCountry
    const selectedLanguage = payload.selectedLanguage ?? localeStore.selectedLanguage

    const { valid } = await validate()
    const isPrivacyPolicyValid = validatePrivacyPolicy()
    const isCountryValid = validateCountry(selectedCountry)
    const isLanguageValid = validateLanguage(selectedLanguage)

    isValid.value = valid && isPrivacyPolicyValid && isCountryValid && isLanguageValid

    if (!isValid.value) return

    try {
      const response = await $fetch('/api/emarsys/subscribe', {
        method: 'PUT',
        body: {
          key_id: 3,
          contact_list_id: EMARSYS_PARAMS.contactListId,
          ...(options.createIfNotExist ? { create_if_not_exist: 1 } : {}),
          contacts: [
            {
              [EMARSYS_PARAMS.email]: values.emailAddress,
              [EMARSYS_PARAMS.optin]: 1,
              [EMARSYS_PARAMS.subscribeSource]: options.subscribeSource,
              [EMARSYS_PARAMS.marketingEmailSubscriber]: 3,
              [EMARSYS_PARAMS.country]: EMARSYS_COUNTRIES[selectedCountry?.toUpperCase()] ?? null,
              [EMARSYS_PARAMS.language]: EMARSYS_LANGUAGES[selectedLanguage] ?? 9,
              [EMARSYS_PARAMS.doubleOptInFlag]: 1
            }
          ]
        }
      })

      if (response.status === 'error') {
        const errorMessage =
          response.data && 'code' in response.data && response.data.code === ALREADY_SUBSCRIBED
            ? t('forms.subscribe.alreadySubscribed')
            : t('global.error.serverError')

        responseError.value = errorMessage
        options.onError?.(errorMessage)

        return
      }

      isConfirmationModalOpen.value = true
      resetForm()

      if (options.requirePrivacyPolicy) {
        privacyPolicyAccepted.value = false
      }

      options.onSuccess?.()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('global.error.serverError')

      responseError.value = t('global.error.serverError')
      options.onError?.(errorMessage)
    }
  }

  return {
    emailAddress,
    privacyPolicyAccepted,
    displayErrors,
    responseError,
    isValid,
    isConfirmationModalOpen,
    closeConfirmationModal,
    clearCountryError,
    clearLanguageError,
    onSubmit
  }
}
